import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, User, X, Calendar, Users, MapPin, LayoutDashboard, LogOut, UtensilsCrossed, Shield, MessageSquare, Bell, BellOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotificationContext } from '../context/NotificationContext';
import CurrencySelector from './CurrencySelector';
import Logo from './Logo';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

// Date helper functions
const getMonthName = (month, year) => {
  return new Date(year, month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const getDaysInMonth = (month, year) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (month, year) => {
  return new Date(year, month, 1).getDay();
};

const formatDisplayDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Date Range Picker Component for Search Modal
const SearchDatePicker = ({ startDate, endDate, selectedMonth, onDateChange, onMonthSelect, onClear }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 90);
  
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectingEnd, setSelectingEnd] = useState(false);
  
  const canGoPrev = viewMonth > today.getMonth() || viewYear > today.getFullYear();
  const canGoNext = new Date(viewYear, viewMonth + 1, 1) <= maxDate;
  
  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  
  const handleDayClick = (day) => {
    const clickedDate = new Date(viewYear, viewMonth, day);
    clickedDate.setHours(0, 0, 0, 0);
    
    if (clickedDate < today || clickedDate > maxDate) return;
    
    if (!selectingEnd || !startDate) {
      onDateChange(clickedDate, null);
      setSelectingEnd(true);
    } else {
      if (clickedDate >= startDate) {
        onDateChange(startDate, clickedDate);
      } else {
        onDateChange(clickedDate, startDate);
      }
      setSelectingEnd(false);
    }
  };
  
  const handleMonthClick = () => {
    const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    onMonthSelect(monthStr);
    setSelectingEnd(false);
  };
  
  const isDateInRange = (day) => {
    if (!startDate || !endDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date >= startDate && date <= endDate;
  };
  
  const isStartDate = (day) => {
    if (!startDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === startDate.getTime();
  };
  
  const isEndDate = (day) => {
    if (!endDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === endDate.getTime();
  };
  
  const isDisabled = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return date < today || date > maxDate;
  };
  
  const isToday = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === today.getTime();
  };
  
  const daysInMonth = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
  
  const days = [];
  for (let i = 0; i < adjustedFirstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  const currentMonthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
  const isMonthSelected = selectedMonth === currentMonthStr;
  
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={`p-1 rounded-full hover:bg-gray-100 ${!canGoPrev ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h3 className="font-semibold">{getMonthName(viewMonth, viewYear)}</h3>
          <button
            onClick={handleMonthClick}
            className={`text-xs px-2 py-1 rounded-full transition-colors ${
              isMonthSelected
                ? 'bg-[#D65A31] text-white'
                : 'text-[#D65A31] hover:bg-[#D65A31]/10'
            }`}
          >
            {isMonthSelected ? 'Month selected' : 'Select entire month'}
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={`p-1 rounded-full hover:bg-gray-100 ${!canGoNext ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => (
          <div key={day ? `day-${day}` : `empty-${index}`} className="aspect-square">
            {day && (
              <button
                onClick={() => handleDayClick(day)}
                disabled={isDisabled(day)}
                className={`w-full h-full flex items-center justify-center text-sm rounded-full transition-colors
                  ${isDisabled(day) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                  ${isStartDate(day) || isEndDate(day) ? 'bg-[#D65A31] text-white hover:bg-[#D65A31]' : ''}
                  ${isDateInRange(day) && !isStartDate(day) && !isEndDate(day) ? 'bg-[#D65A31]/20' : ''}
                  ${isToday(day) && !isStartDate(day) && !isEndDate(day) ? 'border-2 border-[#D65A31]' : ''}
                `}
              >
                {day}
              </button>
            )}
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {selectingEnd && startDate ? 'Select end date' : 'Select start date or entire month'}
          </div>
          {(startDate || endDate || selectedMonth) && (
            <button
              onClick={onClear}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Search up to 90 days from today
        </p>
      </div>
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { unreadCount, enabled, toggleNotifications, permission } = useNotificationContext();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchData, setSearchData] = useState({
    location: '',
    startDate: null,
    endDate: null,
    selectedMonth: null,
    guests: 1
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestRef = useRef(null);

  const allCuisines = ['American', 'Chinese', 'French', 'Greek', 'Indian', 'Italian', 'Japanese', 'Korean', 'Mediterranean', 'Mexican', 'Middle Eastern', 'Spanish', 'Thai', 'British', 'Emirati', 'Singaporean', 'Peranakan', 'Thai-Chinese', 'Royal Thai', 'Southern Thai', 'Isaan Thai', 'Modern Thai', 'Lebanese', 'Persian', 'Moroccan', 'Malay'];
  const allCities = [
    'London, UK', 'Paris, France', 'Dubai, UAE',
    'Bangkok, Thailand', 'Singapore',
    'New York, NY', 'Los Angeles, CA', 'San Francisco, CA', 'Chicago, IL',
    'Miami, FL', 'Austin, TX', 'Seattle, WA', 'Portland, OR', 'Boston, MA',
    'Rome, Italy', 'Barcelona, Spain', 'Abu Dhabi, UAE',
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationChange = (val) => {
    setSearchData({ ...searchData, location: val });
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    const q = val.toLowerCase();
    const matchedCuisines = allCuisines.filter(c => c.toLowerCase().includes(q)).map(c => ({ type: 'cuisine', value: c }));
    const matchedCities = allCities.filter(c => c.toLowerCase().includes(q)).map(c => ({ type: 'city', value: c }));
    const combined = [...matchedCuisines, ...matchedCities].slice(0, 8);
    setSuggestions(combined);
    setShowSuggestions(combined.length > 0);
  };

  const selectSuggestion = (item) => {
    setSearchData({ ...searchData, location: item.value });
    setShowSuggestions(false);
  };

  // Date display text for search modal
  const dateDisplayText = useMemo(() => {
    if (searchData.selectedMonth) {
      const [year, month] = searchData.selectedMonth.split('-');
      return getMonthName(parseInt(month) - 1, parseInt(year));
    }
    if (searchData.startDate && searchData.endDate) {
      return `${formatDisplayDate(searchData.startDate)} - ${formatDisplayDate(searchData.endDate)}`;
    }
    if (searchData.startDate) {
      return formatDisplayDate(searchData.startDate);
    }
    return null;
  }, [searchData.startDate, searchData.endDate, searchData.selectedMonth]);

  const handleDateChange = (start, end) => {
    setSearchData({ ...searchData, startDate: start, endDate: end, selectedMonth: null });
  };

  const handleMonthSelect = (month) => {
    setSearchData({ ...searchData, selectedMonth: month, startDate: null, endDate: null });
  };

  const clearDateSelection = () => {
    setSearchData({ ...searchData, startDate: null, endDate: null, selectedMonth: null });
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchData.location) params.set('location', searchData.location);
    if (searchData.selectedMonth) {
      params.set('month', searchData.selectedMonth);
    } else if (searchData.startDate) {
      params.set('start_date', format(searchData.startDate, 'yyyy-MM-dd'));
      if (searchData.endDate) {
        params.set('end_date', format(searchData.endDate, 'yyyy-MM-dd'));
      }
    }
    if (searchData.guests) params.set('guests', searchData.guests);
    navigate(`/search?${params.toString()}`);
    setSearchOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50" data-testid="navbar">
        <div className="max-w-[2000px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="flex items-center justify-between h-20 overflow-visible">
            {/* Logo */}
            <div data-testid="logo-link" className="mr-6 shrink-0">
              <Logo size={34} />
            </div>

            {/* Search Bar - Desktop */}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex items-center gap-4 border border-gray-300 rounded-full py-2 px-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              data-testid="search-bar"
            >
              <span className="font-medium text-sm">Location</span>
              <span className="border-l border-gray-300 h-6"></span>
              <span className="font-medium text-sm">Date</span>
              <span className="border-l border-gray-300 h-6"></span>
              <span className="text-gray-500 text-sm">Foodies attending</span>
              <div className="bg-[#D65A31] p-1.5 rounded-full">
                <Search className="w-3.5 h-3.5 text-white" />
              </div>
            </button>

            {/* Search Bar - Mobile */}
            <button
              onClick={() => setSearchOpen(true)}
              className="md:hidden flex items-center gap-3 border border-gray-300 rounded-full py-2 px-4 shadow-sm flex-1 mx-3 min-w-0"
              data-testid="mobile-search-bar"
            >
              <Search className="w-4 h-4 shrink-0" />
              <div className="text-left truncate">
                <p className="text-sm font-medium">Find an experience</p>
              </div>
            </button>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Messages Link with Badge - Only for authenticated users */}
              {isAuthenticated && (
                <Link
                  to="/messages"
                  className="relative hidden md:flex hover:bg-gray-100 p-3 rounded-full transition-colors"
                  data-testid="messages-nav-link"
                >
                  <MessageSquare className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span 
                      className="absolute -top-0.5 -right-0.5 bg-[#D65A31] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                      data-testid="unread-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              <Link
                to="/host"
                className="hidden md:block font-medium text-sm hover:bg-gray-100 px-4 py-3 rounded-full transition-colors"
                data-testid="host-link"
              >
                Host an experience
              </Link>
              <div className="hidden md:flex hover:bg-gray-100 p-3 rounded-full transition-colors">
                <CurrencySelector />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-gray-300 rounded-full py-2 px-3 hover:shadow-md transition-shadow"
                    data-testid="user-menu-btn"
                  >
                    <Menu className="w-4 h-4" />
                    <div className="bg-gray-500 rounded-full p-1">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-2">
                  {isAuthenticated ? (
                    <>
                      <div className="px-2 py-2 border-b">
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-sm text-gray-500">{user?.email}</p>
                        <p className="text-xs text-gray-400 capitalize mt-0.5">{user?.role === 'user' ? 'Guest' : user?.role}</p>
                      </div>
                      {/* Admin only - Admin Panel */}
                      {user?.role === 'admin' && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer flex items-center gap-2 text-[#D65A31] font-medium">
                            <Shield className="w-4 h-4" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {/* Host/Admin only - Dashboard */}
                      {(user?.role === 'host' || user?.role === 'admin') && (
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard" className="cursor-pointer flex items-center gap-2">
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/my-bookings" className="cursor-pointer flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          My Bookings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/messages" className="cursor-pointer flex items-center gap-2" data-testid="messages-menu-item">
                          <MessageSquare className="w-4 h-4" />
                          Messages
                          {unreadCount > 0 && (
                            <span className="ml-auto bg-[#D65A31] text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="cursor-pointer flex items-center gap-2" data-testid="profile-menu-item">
                          <User className="w-4 h-4" />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      {/* Host only - Create Experience */}
                      {(user?.role === 'host' || user?.role === 'admin') && (
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard/create" className="cursor-pointer flex items-center gap-2">
                            <UtensilsCrossed className="w-4 h-4" />
                            Create Experience
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="cursor-pointer flex items-center gap-2"
                        onClick={toggleNotifications}
                        data-testid="notification-toggle"
                      >
                        {enabled ? (
                          <>
                            <Bell className="w-4 h-4 text-green-600" />
                            <span>Notifications On</span>
                            <span className="ml-auto text-xs text-green-600">●</span>
                          </>
                        ) : (
                          <>
                            <BellOff className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Enable Notifications</span>
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="cursor-pointer text-red-600 flex items-center gap-2"
                        onClick={() => { logout(); navigate('/'); }}
                      >
                        <LogOut className="w-4 h-4" />
                        Log out
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/register" className="font-medium cursor-pointer" data-testid="signup-menu-item">
                          Sign up
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/login" className="cursor-pointer" data-testid="login-menu-item">
                          Log in
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/host" className="cursor-pointer">Host an experience</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer">
                        Help Center
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-3xl p-0 gap-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">Find your next meal experience</h2>
              <button onClick={() => setSearchOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Location with auto-suggest */}
              <div className="border rounded-xl p-4 hover:border-gray-400 transition-colors relative" ref={suggestRef}>
                <label className="text-xs font-medium text-gray-600 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Where
                </label>
                <Input
                  placeholder="Search cities or cuisines"
                  className="border-0 p-0 text-base focus-visible:ring-0 placeholder:text-gray-400"
                  value={searchData.location}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                  autoComplete="off"
                  data-testid="search-location-input"
                />
                {showSuggestions && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="search-suggestions">
                    {suggestions.map((item, i) => (
                      <button
                        key={`${item.type}-${item.value}`}
                        onClick={() => selectSuggestion(item)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                        data-testid={`suggestion-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                          {item.type === 'city' ? <MapPin className="w-4 h-4 text-gray-500" /> : <UtensilsCrossed className="w-4 h-4 text-gray-500" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{item.value}</p>
                          <p className="text-xs text-gray-400 capitalize">{item.type}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Range Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <div className="border rounded-xl p-4 hover:border-gray-400 transition-colors cursor-pointer" data-testid="date-picker">
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> When
                    </label>
                    <p className={`text-base ${dateDisplayText ? 'text-gray-900' : 'text-gray-400'}`}>
                      {dateDisplayText || 'Add dates'}
                    </p>
                    {dateDisplayText && (
                      <p className="text-xs text-gray-500 mt-1">
                        {searchData.selectedMonth ? 'Entire month' : 'Date range'}
                      </p>
                    )}
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <SearchDatePicker
                    startDate={searchData.startDate}
                    endDate={searchData.endDate}
                    selectedMonth={searchData.selectedMonth}
                    onDateChange={handleDateChange}
                    onMonthSelect={handleMonthSelect}
                    onClear={clearDateSelection}
                  />
                </PopoverContent>
              </Popover>

              {/* Guests */}
              <div className="border rounded-xl p-4 hover:border-gray-400 transition-colors">
                <label className="text-xs font-medium text-gray-600 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Guests
                </label>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => setSearchData({ ...searchData, guests: Math.max(1, searchData.guests - 1) })}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-600 transition-colors"
                    data-testid="guests-minus-btn"
                  >
                    -
                  </button>
                  <span className="text-base font-medium w-8 text-center" data-testid="guests-count">{searchData.guests}</span>
                  <button
                    onClick={() => setSearchData({ ...searchData, guests: searchData.guests + 1 })}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-600 transition-colors"
                    data-testid="guests-plus-btn"
                  >
                    +
                  </button>
                  <span className="text-gray-500 text-sm">diners</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <button
                onClick={() => setSearchData({ location: '', startDate: null, endDate: null, selectedMonth: null, guests: 1 })}
                className="text-sm font-medium underline"
                data-testid="clear-search-btn"
              >
                Clear all
              </button>
              <Button
                onClick={handleSearch}
                className="bg-[#D65A31] hover:bg-[#B84A28] text-white px-6 py-3 rounded-lg flex items-center gap-2"
                data-testid="search-submit-btn"
              >
                <Search className="w-4 h-4" />
                Search
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
